import React from 'react';
import { StartOrEndMarker } from './cards/StartOrEndMarker';
import {
  InteractionInfo,
  InvisibleNotificationProps,
  VideoOverlayTypes,
} from '$/components/Interactions/InteractionInfo';
import { ModalValidationRules } from '$/components/Interactions/types';
import * as yup from 'yup';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import { theme } from 'twin.macro';
import formatTimeInWebinarSecs from '$/utils/formatTimeInWebinarSecs';
import { useCardContext } from './cards/CardContext';
import {
  AttendeeFragment,
  InteractionForAttendeeFragment,
  InteractionFragment,
  PublicEWebinarFragment,
} from '$/GraphQL/schema/schema';

const InvisibleNotification: React.FC = () => {
  return null;
};

const OverlayCard: React.FC = (): JSX.Element | null => {
  const { interaction } = useCardContext();

  const isEndAt = interaction.id?.endsWith('endsAt');

  return (
    <StartOrEndMarker
      text={isEndAt ? 'End' : 'Start'}
      color={isEndAt ? theme`colors.red.2`.toString() : theme`colors.green.2`.toString()}
      mode={PageMode.edit}
    />
  );
};

export abstract class OverlayInteractionInfo extends InteractionInfo {
  public constructor(
    i: InteractionForAttendeeFragment | InteractionFragment,
    ewebinar?: PublicEWebinarFragment,
    public attendee?: AttendeeFragment
  ) {
    super(i, ewebinar, attendee);
  }

  public hasEndsAt(): boolean {
    return true;
  }

  public hasPinToTopToggle(): boolean {
    return false;
  }

  public hasCard(_isPreview?: boolean): boolean {
    return false;
  }

  public hasVideoOverlay(): VideoOverlayTypes | null {
    return 'over-video';
  }

  public notificationComponentForAttendee(): React.FC<InvisibleNotificationProps> | null {
    return InvisibleNotification;
  }

  public isInteractiveCard(): boolean {
    return true;
  }

  public modalValidationRules(): ModalValidationRules {
    return {
      endsAt: yup
        .number()
        .typeError('Time must be specified in -HH:MM:SS or -MM:SS format.')
        .required()
        .when('playbackPosition', ([playbackPosition]: number[], schema: yup.NumberSchema) => {
          if (!this.ewebinar) return schema;
          schema = schema.min(
            Math.max(0, playbackPosition),
            `Minimum position is ${formatTimeInWebinarSecs(Math.max(0, playbackPosition))}`
          );
          schema = schema.max(
            this.ewebinar.duration,
            `Maximum position is ${formatTimeInWebinarSecs(this.ewebinar.duration)}`
          );
          return schema;
        }),
      overlaySettings: yup.object().shape({
        position: yup.array(yup.number()).required(),
      }),
      title: yup.string().required().max(50, 'Image name maximum is 50 characters'),
    };
  }

  public cardComponent(): React.FC<{ onClickHeader?: () => void }> {
    return OverlayCard;
  }

  public cardBodyComponent(): React.FC {
    return (): JSX.Element | null => null;
  }
}
